<template>
  <div class="formulario-modelo">
    <div class="header-flex">
      <div>
        <h1>Asignar tareas por indicador</h1>
        <div class="ctx-proceso">
          <span v-if="nombreProceso" class="ctx-badge">{{ nombreProceso }}</span>
          <span v-if="nombreVersion" class="ctx-badge">{{ nombreVersion }}</span>
          <span v-if="ambito === 'institucional' && nombreCampus" class="ctx-badge">Campus: {{ nombreCampus }}</span>
        </div>
      </div>
      <button class="btn salir" @click="$router.push({ name: 'ProcesoAutoevaluacion' })">Volver</button>
    </div>

    <div class="divider"></div>

    <p class="ayuda">
      <template v-if="ambito === 'institucional'">
        Asigna un responsable por factor. El sistema asignará a esa persona en todos los indicadores del factor, para subir evidencias y calificar.
      </template>
      <template v-else>
        Asigna, por indicador, quién <b>sube evidencias o aplica instrumentos</b> y quién <b>califica</b>.
        Los cambios se guardan al seleccionar.
      </template>
    </p>

    <div v-if="cargando" class="info">Cargando indicadores y usuarios…</div>

    <div v-else class="marco">
      <div v-if="!indicadores.length" class="texto-vacio">No hay indicadores registrados para esta versión.</div>

      <!-- ── Modo institucional: un responsable por factor ── -->
      <template v-else-if="ambito === 'institucional'">
        <div class="contenedor-tabla">
          <table class="lista-tabla">
            <thead>
              <tr>
                <th>Factores</th>
                <th class="col-num-th">Indicadores</th>
                <th class="col-tarea">Responsable</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="factor in componentesPrimerNivel" :key="factor.id_componente">
                <td class="nombre">
                  <span v-if="formatOrdinal(factor.ordinal)" class="ordinal">
                    {{ formatOrdinal(factor.ordinal) }}
                  </span>
                  {{ factor.nombre }}
                </td>
                <td class="col-num">
                  {{ indicadoresPorFactor.get(factor.id_componente!)?.length ?? 0 }}
                </td>
                <td class="col-tarea">
                  <div class="factor-selector">
                    <select
                      class="input-select"
                      :disabled="guardandoFactor === factor.id_componente"
                      :value="seleccionFactor[factor.id_componente!] ?? ''"
                      @change="onChangeFactor(factor.id_componente!, $event)"
                    >
                      <option value="">— Sin asignar —</option>
                      <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.name }}</option>
                    </select>
                    <span v-if="guardandoFactor === factor.id_componente" class="factor-guardando">Guardando…</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- ── Modo programa: tabla indicador por indicador ── -->
      <template v-else>
        <div class="barra-herramientas">
          <div class="herramienta-grupo" v-if="componentesPrimerNivel.length">
            <span class="herramienta-label">Filtrar por factor</span>
            <select v-model="componenteFiltro" class="input-select input-select-sm">
              <option :value="null">Todos</option>
              <option v-for="c in componentesPrimerNivel" :key="c.id_componente" :value="c.id_componente">
                {{ formatOrdinal(c.ordinal) ? formatOrdinal(c.ordinal) + '. ' + c.nombre : c.nombre }}
              </option>
            </select>
          </div>

          <div class="herramienta-sep" v-if="componentesPrimerNivel.length"></div>

          <div class="herramienta-grupo">
            <span class="herramienta-label">Asignación masiva</span>
            <div class="bulk-fila">
              <select v-model="usuarioBulk" class="input-select input-select-sm">
                <option :value="null" disabled>— Selecciona usuario —</option>
                <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
              <select v-model="tareaBulk" class="input-select input-select-sm">
                <option value="ambas">Ambas tareas</option>
                <option value="evidencias">Subir / Aplicar</option>
                <option value="calificar">Calificar</option>
              </select>
              <button
                class="btn guardar"
                :disabled="!usuarioBulk || guardandoBulk"
                @click="asignarMasivo"
              >
                {{ guardandoBulk ? 'Asignando...' : componenteFiltro !== null ? 'Asignar visibles' : 'Asignar todos' }}
              </button>
            </div>
            <p v-if="componenteFiltro !== null" class="aviso-filtro">
              Solo se asignarán los {{ indicadoresFiltrados.length }} indicador(es) del componente seleccionado. Para asignar todos, quita el filtro.
            </p>
          </div>
        </div>

        <div class="contenedor-tabla">
          <table class="lista-tabla">
            <thead>
              <tr>
                <th class="col-componente-th">Elementos</th>
                <th>Indicador</th>
                <th v-for="t in tareas" :key="t.id" class="col-tarea">{{ t.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="indicadoresFiltrados.length === 0">
                <td :colspan="2 + tareas.length" class="texto-vacio-celda">
                  No hay indicadores para el componente seleccionado.
                </td>
              </tr>
              <tr v-for="ind in indicadoresFiltrados" :key="ind.id_indicador">
                <td class="col-componente">
                  <div v-if="rutaComponenteMap.get(ind.id_componente)?.length" class="comp-ruta">
                    {{ rutaComponenteMap.get(ind.id_componente)!.join(' › ') }}
                  </div>
                  {{ nombreComponenteMap.get(ind.id_componente) ?? '—' }}
                </td>
                <td class="nombre">
                  <span v-if="ind.ordinal" class="ordinal">{{ ind.ordinal }}</span>
                  {{ ind.enunciado || ('Indicador #' + ind.id_indicador) }}
                </td>
                <td v-for="t in tareas" :key="t.id" class="col-tarea">
                  <select
                    class="input-select"
                    :disabled="guardandoClave === clave(ind.id_indicador!, t.id) || guardandoBulk"
                    :value="seleccion[clave(ind.id_indicador!, t.id)] ?? ''"
                    @change="onChange(ind.id_indicador!, t.id, $event)"
                  >
                    <option value="">— Sin asignar —</option>
                    <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <MensajeFlotante :visible="msgVisible" :mensaje="msgTexto" :tipo="msgTipo" @cerrar="msgVisible = false" />
  </div>
</template>

<script lang="ts" setup>
/**
 * Reparto de indicadores que hace el director de programa.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/ejecucion/:id_ejecucion/asignar-tareas` (AsignarTareasDirector) — permiso `procesos.asignar`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useComponentes } from '@/store/GestionComponentes'
import { getProcesoByIdService } from '@/services/ProcesoAutoevaluacionService'
import { crearIndicadorEvaluadorService, eliminarIndicadorEvaluadorService } from '@/services/IndicadorEvaluadorService'
import { listarUsuarios } from '@/services/RegistroUsuariosService'
import useAutenticar from '@/store/IngresoAutenticar'
import type Indicador from '@/interfaces/Indicador'
import type Componente from '@/interfaces/Componente'
import type { Usuario } from '@/interfaces/Usuario'
import type { TareaAsignacion } from '@/interfaces/IndicadorEvaluador'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const route = useRoute()
const idEjecucion = Number(route.params.id_ejecucion)

const indicadoresStore = useGestionIndicadores()
const evaluadoresStore = useGestionIndicadorEvaluadores()
const versionesStore   = useGestionVersiones()
const componentesStore = useComponentes()
const authStore        = useAutenticar()

const tareas: { id: TareaAsignacion; label: string }[] = [
  { id: 'evidencias', label: 'Subir / Aplicar Instrumento' },
  { id: 'calificar',  label: 'Calificar' }
]

const cargando      = ref(true)
const idVersion     = ref<number | null>(null)
const ambito        = ref<'institucional' | 'programa' | null>(null)
const nombreProceso = ref('Proceso de autoevaluación')
const nombreVersion = ref('')
const nombreCampus  = ref('')
const indicadores   = ref<Indicador[]>([])
const usuarios      = ref<Usuario[]>([])

const guardandoClave  = ref<string | null>(null)
const guardandoBulk   = ref(false)
const guardandoFactor = ref<number | null>(null)

const componentesPrimerNivel = ref<Componente[]>([])
const componenteFiltro       = ref<number | null>(null)
const nombreComponenteMap    = ref(new Map<number, string>())
const rutaComponenteMap      = ref(new Map<number, string[]>())
const ancestroMap            = ref(new Map<number, number>())

const usuarioBulk = ref<number | null>(null)
const tareaBulk   = ref<'evidencias' | 'calificar' | 'ambas'>('ambas')

const seleccionFactor = reactive<Record<number, number | null>>({})

const indicadoresPorFactor = computed(() => {
  const mapa = new Map<number, number[]>()
  for (const factor of componentesPrimerNivel.value) {
    if (!factor.id_componente) continue
    mapa.set(
      factor.id_componente,
      indicadores.value
        .filter(i => i.id_indicador != null && ancestroMap.value.get(i.id_componente) === factor.id_componente)
        .map(i => i.id_indicador!)
    )
  }
  return mapa
})

const seleccion          = reactive<Record<string, number | null>>({})
const asignacionPorClave = reactive<Record<string, { id: number; id_usuario: number }>>({})

const msgVisible = ref(false)
const msgTexto   = ref('')
const msgTipo    = ref<'positivo' | 'error'>('positivo')

const indicadoresFiltrados = computed(() =>
  componenteFiltro.value === null
    ? indicadores.value
    : indicadores.value.filter(i => ancestroMap.value.get(i.id_componente) === componenteFiltro.value)
)

function clave(idInd: number, tarea: TareaAsignacion): string {
  return `${idInd}:${tarea}`
}

function toast(texto: string, tipo: 'positivo' | 'error' = 'positivo') {
  msgTexto.value   = texto
  msgTipo.value    = tipo
  msgVisible.value = true
}

function formatOrdinal(ordinal: string | null | undefined): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

const compararOrdinal = (a: string, b: string): number => {
  const pa = a.split('.').map(n => parseInt(n, 10) || 0)
  const pb = b.split('.').map(n => parseInt(n, 10) || 0)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

const flattenNombres = (nodo: Componente, mapa: Map<number, string>, rutaMapa: Map<number, string[]>, ancestros: string[] = []): void => {
  const ord = formatOrdinal(nodo.ordinal)
  const label = ord ? `${ord}. ${nodo.nombre ?? ''}` : (nodo.nombre ?? '')
  if (nodo.id_componente) {
    mapa.set(nodo.id_componente, label)
    rutaMapa.set(nodo.id_componente, [...ancestros])
  }
  for (const h of nodo.children ?? []) flattenNombres(h, mapa, rutaMapa, [...ancestros, label])
}

const mapAncestros = (nodos: Componente[], ancestroId: number, mapa: Map<number, number>): void => {
  for (const n of nodos) {
    if (n.id_componente) mapa.set(n.id_componente, ancestroId)
    if (n.children?.length) mapAncestros(n.children, ancestroId, mapa)
  }
}

async function onChange(idInd: number, tarea: TareaAsignacion, ev: Event) {
  const val = (ev.target as HTMLSelectElement).value
  await guardarCelda(idInd, tarea, val ? Number(val) : null)
}

async function guardarCeldaBulk(idInd: number, tarea: TareaAsignacion, nuevo: number | null) {
  const key       = clave(idInd, tarea)
  const existente = asignacionPorClave[key]
  if ((existente?.id_usuario ?? null) === nuevo) return

  if (existente) {
    const del = await eliminarIndicadorEvaluadorService(existente.id)
    if (!del.ok) return
    delete asignacionPorClave[key]
  }
  if (nuevo) {
    const r = await crearIndicadorEvaluadorService({
      id_version:      idVersion.value!,
      id_ejecucion:    idEjecucion,
      id_indicador:    idInd,
      id_usuario:      nuevo,
      tarea,
      id_asignado_por: authStore.usuarioActual?.id ?? null
    })
    if (r.ok && r.data?.id) {
      asignacionPorClave[key] = { id: r.data.id, id_usuario: nuevo }
      seleccion[key] = nuevo
    } else {
      seleccion[key] = null
    }
  } else {
    seleccion[key] = null
  }
}

async function guardarCelda(idInd: number, tarea: TareaAsignacion, nuevo: number | null) {
  const key      = clave(idInd, tarea)
  const existente = asignacionPorClave[key]
  if ((existente?.id_usuario ?? null) === nuevo) return

  guardandoClave.value = key
  const previo = seleccion[key] ?? null

  if (existente) {
    const del = await evaluadoresStore.eliminar(existente.id)
    if (!del.ok) {
      guardandoClave.value = null
      toast('No se pudo actualizar la asignación.', 'error')
      seleccion[key] = previo
      return
    }
    delete asignacionPorClave[key]
  }

  if (nuevo) {
    const r = await evaluadoresStore.asignar({
      id_version:      idVersion.value!,
      id_ejecucion:    idEjecucion,
      id_indicador:    idInd,
      id_usuario:      nuevo,
      tarea,
      id_asignado_por: authStore.usuarioActual?.id ?? null
    })
    if (r.ok && r.data?.id) {
      asignacionPorClave[key] = { id: r.data.id, id_usuario: nuevo }
      seleccion[key] = nuevo
      toast('Asignación guardada.')
    } else {
      seleccion[key] = null
      toast('No se pudo guardar la asignación.', 'error')
    }
  } else {
    seleccion[key] = null
    toast('Asignación retirada.')
  }

  guardandoClave.value = null
}

function derivarSeleccionFactores() {
  for (const factor of componentesPrimerNivel.value) {
    if (!factor.id_componente) continue
    const inds = indicadoresPorFactor.value.get(factor.id_componente) ?? []
    if (!inds.length) { seleccionFactor[factor.id_componente] = null; continue }
    const primero = seleccion[clave(inds[0], 'calificar')] ?? null
    seleccionFactor[factor.id_componente] = inds.every(
      id => (seleccion[clave(id, 'calificar')] ?? null) === primero
    ) ? primero : null
  }
}

async function onChangeFactor(idFactor: number, ev: Event) {
  const val = (ev.target as HTMLSelectElement).value
  await asignarPorFactor(idFactor, val ? Number(val) : null)
}

async function asignarPorFactor(idFactor: number, idUsuario: number | null) {
  if (guardandoFactor.value !== null) return
  guardandoFactor.value = idFactor
  seleccionFactor[idFactor] = idUsuario
  try {
    const inds = indicadoresPorFactor.value.get(idFactor) ?? []
    const pares = inds.flatMap(idInd =>
      (['evidencias', 'calificar'] as TareaAsignacion[]).map(t => ({ idInd, tarea: t }))
    )
    for (let i = 0; i < pares.length; i += 10) {
      const lote = pares.slice(i, i + 10)
      await Promise.allSettled(
        lote.map(({ idInd, tarea }) => guardarCeldaBulk(idInd, tarea, idUsuario))
      )
    }
    toast(idUsuario ? 'Responsable asignado.' : 'Asignación retirada.')
  } catch {
    toast('Error al asignar el responsable.', 'error')
    seleccionFactor[idFactor] = null
  } finally {
    guardandoFactor.value = null
  }
}

async function asignarMasivo() {
  if (!usuarioBulk.value || guardandoBulk.value) return
  guardandoBulk.value = true
  try {
    const tareasSeleccionadas: TareaAsignacion[] =
      tareaBulk.value === 'ambas' ? ['evidencias', 'calificar'] : [tareaBulk.value]
    const pares = indicadoresFiltrados.value.flatMap(ind =>
      tareasSeleccionadas.map(t => ({ idInd: ind.id_indicador!, tarea: t }))
    )
    for (let i = 0; i < pares.length; i += 10) {
      const lote = pares.slice(i, i + 10)
      await Promise.allSettled(
        lote.map(({ idInd, tarea }) => guardarCeldaBulk(idInd, tarea, usuarioBulk.value!))
      )
    }
    toast('Asignación masiva completada.')
  } catch {
    toast('Error durante la asignación masiva.', 'error')
  } finally {
    guardandoBulk.value = false
  }
}

onMounted(async () => {
  try {
    const [proceso, usrs] = await Promise.all([
      getProcesoByIdService(idEjecucion),
      listarUsuarios(),
      indicadoresStore.cargarTodos()
    ])
    usuarios.value = usrs

    if (!proceso) {
      toast('No se encontró la ejecución.', 'error')
      return
    }

    idVersion.value     = proceso.id_version
    ambito.value        = proceso.ambito
    nombreProceso.value = `${proceso.ambito === 'programa' ? 'Programa' : 'Institucional'} · ${proceso.periodo}`
    nombreCampus.value  = proceso.campus?.nombre ?? ''

    const version = await versionesStore.getVersionById(proceso.id_version)
    nombreVersion.value = version?.nombre ?? ''

    indicadores.value = indicadoresStore.indicadores
      .filter(i => i.id_version === idVersion.value && i.id_indicador != null)
      .sort((a, b) => ((a.ordinal ?? 9999) - (b.ordinal ?? 9999)) || (a.id_indicador! - b.id_indicador!))

    // Build component name map and first-level ancestor map
    if (version?.id_modelo) {
      const raiz = await componentesStore.cargarRaizPorModelo(version.id_modelo)
      if (raiz?.id_componente) {
        await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)
        const raizNodo = componentesStore.componentes[0]
        if (raizNodo?.children?.length) {
          componentesPrimerNivel.value = [...raizNodo.children].sort(
            (a, b) => compararOrdinal(a.ordinal ?? '0', b.ordinal ?? '0')
          )
          const nMap = new Map<number, string>()
          const rMap = new Map<number, string[]>()
          const aMap = new Map<number, number>()
          for (const factor of raizNodo.children) {
            if (!factor.id_componente) continue
            flattenNombres(factor, nMap, rMap)
            aMap.set(factor.id_componente, factor.id_componente)
            mapAncestros(factor.children ?? [], factor.id_componente, aMap)
          }
          nombreComponenteMap.value = nMap
          rutaComponenteMap.value = rMap
          ancestroMap.value = aMap
        }
      }
    }

    await evaluadoresStore.cargar({ id_version: idVersion.value!, id_ejecucion: idEjecucion })
    for (const a of evaluadoresStore.asignaciones) {
      if (a.id_indicador == null || a.tarea == null || a.id == null) continue
      const key = clave(a.id_indicador, a.tarea)
      seleccion[key] = a.id_usuario
      asignacionPorClave[key] = { id: a.id, id_usuario: a.id_usuario }
    }
    if (proceso.ambito === 'institucional') derivarSeleccionFactores()
  } catch (e: any) {
    toast(e?.message || 'Error al cargar los datos.', 'error')
  } finally {
    cargando.value = false
  }
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white; padding: 2rem; border-radius: var(--radius-lg);
  flex: 1; min-height: 0; overflow-y: auto;
  box-shadow: var(--shadow-md); box-sizing: border-box; margin: 20px; font-family: var(--font-family);
}
.marco {
  border: 2px solid rgb(156, 155, 155);
  box-sizing: border-box;
  padding: 20px;
}
.header-flex { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
h1 { font-size: 1.4rem; font-weight: 700; margin: 0; color: var(--text-primary); }
.subtitulo { font-size: 0.85rem; color: #666; margin: 0.25rem 0 0; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }
.ayuda { font-size: 0.85rem; color: #555; margin: 0 0 1rem; }
.info { padding: 1rem 0; color: #555; }
.texto-vacio { padding: 1rem 0; color: #8f8e8e; font-size: 0.9rem; }

.barra-herramientas {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
}
.herramienta-grupo { display: flex; flex-direction: column; gap: 0.35rem; }
.herramienta-label { font-size: 0.75rem; font-weight: 600; color: #555; text-transform: uppercase; letter-spacing: 0.04em; }
.herramienta-sep { width: 1px; background: #ddd; align-self: stretch; margin: 0 0.25rem; }
.bulk-fila { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.aviso-filtro {
  margin: 0.4rem 0 0;
  font-size: 0.8rem;
  color: #7a5800;
  background: #fff8e1;
  border: 1px solid #eea222;
  border-radius: 6px;
  padding: 0.35rem 0.65rem;
}

.input-select-sm { max-width: 200px; }

.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
.lista-tabla th,
.lista-tabla td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #e0e0e0; vertical-align: middle; }
.lista-tabla thead { background-color: #0e69a0; color: white; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.03em; }
.col-tarea { width: 210px; }
.col-componente-th { width: 240px; min-width: 200px; padding-right: 1.5rem; }
.col-componente { color: #555; font-size: 0.82rem; width: 240px; min-width: 200px; padding-right: 1.5rem; white-space: normal; word-break: break-word; }
.nombre { font-weight: 500; }
.ordinal { display: inline-block; min-width: 2.2em; color: #0e69a0; font-weight: 700; }
.texto-vacio-celda { color: #8f8e8e; font-size: 0.9rem; padding: 1rem; text-align: center; }

.input-select {
  padding: 0.4rem 0.6rem; border: 1px solid #e6e5e5; border-radius: var(--radius-md);
  font-size: 0.85rem; font-family: var(--font-family); background-color: #f9f9f9; width: 100%;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.input-select:focus { outline: none; border-color: #0e69a0; box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1); }
.input-select:disabled { opacity: 0.6; }

.btn {
  padding: 0.55rem 1.1rem; font-weight: 600; border: none; border-radius: 8px;
  cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.85rem;
  white-space: nowrap;
}
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:hover:not(:disabled) { background-color: #0e69a0; color: white; }
.btn.guardar:disabled { opacity: 0.5; cursor: not-allowed; }

.ctx-proceso { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.4rem; }
.ctx-badge {
  display: inline-block;
  padding: 2px 10px;
  background: #e8f0f8;
  border-left: 3px solid #0e69a0;
  border-radius: 3px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #111;
}

.comp-ruta { font-size: 0.72rem; color: #999; margin-bottom: 0.2rem; line-height: 1.4; }

/* Modo institucional */
.col-num-th { width: 100px; text-align: center; }
.col-num { text-align: center; color: #555; }
.factor-selector { display: flex; align-items: center; gap: 0.5rem; }
.factor-selector .input-select { flex: 1; }
.factor-guardando { font-size: 0.78rem; color: #888; white-space: nowrap; }
</style>
